/**
 * Minutedrone t92 (lot 3) : bloc « Compléter votre commande ».
 *
 * - Sous-total des accessoires cochés (quantité × prix), en TTC ou en HT selon la bascule de la fiche
 *   (assets/minutedrone-tax-toggle.js, clé locale « minutedrone-tax-mode ») ; le sous-total porte data-tax-price,
 *   la bascule le met donc à jour elle-même quand le visiteur change de mode.
 * - « Sélection (n articles) » : n = somme des quantités cochées ; forme au singulier pour 0 et 1 (règle du français).
 * - « Ajouter la sélection au panier » : un seul POST /cart/add.js avec items[], puis rafraîchissement du panier comme le
 *   fait le formulaire d'achat d'Horizon (CartAddEvent avec les sections du tiroir, nombre d'articles du panier).
 * - Fiches « exclusives » (DJI Care) : quantité 1, cocher l'une décoche l'autre.
 * - Les événements de quantité de ce bloc sont arrêtés ici : la barre d'achat collée ne doit compter que le drone.
 */
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent } from '@theme/events';
import '@theme/component-quantity-selector';

const SOURCE = 'md-articles-lies';
const CLE_MODE_TAXE = 'minutedrone-tax-mode';

/** @returns {{ cart_add_url: string, cart_url: string }} */
function routes() {
  const theme = typeof Theme !== 'undefined' ? /** @type {any} */ (Theme) : null;
  return {
    cart_add_url: theme?.routes?.cart_add_url || '/cart/add.js',
    cart_url: theme?.routes?.cart_url || '/cart',
  };
}

/** @returns {'ht' | 'ttc'} */
function modeTaxe() {
  try {
    return window.localStorage.getItem(CLE_MODE_TAXE) === 'ht' ? 'ht' : 'ttc';
  } catch (erreur) {
    return 'ttc';
  }
}

/**
 * Même format que la bascule HT/TTC de la fiche (Intl, fr-FR).
 * @param {number} centimes
 * @param {string} devise
 */
function formaterPrix(centimes, devise) {
  const montant = (Number(centimes) || 0) / 100;
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise || 'EUR' }).format(montant);
  } catch (erreur) {
    return `${montant.toFixed(2)} ${devise || 'EUR'}`;
  }
}

/** @returns {Promise<{ item_count: number, items: Array<{ variant_id: number, quantity: number }> } | null>} */
async function lirePanier() {
  try {
    const reponse = await fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!reponse.ok) return null;
    return await reponse.json();
  } catch (erreur) {
    return null;
  }
}

/**
 * <md-articles-lies>
 *
 * @typedef {object} RefsArticles
 * @property {HTMLElement} compte - « Sélection (n articles) ».
 * @property {HTMLElement} total - Sous-total (porte data-tax-price).
 * @property {HTMLButtonElement} bouton - « Ajouter la sélection au panier ».
 * @property {HTMLElement} [erreur] - Message d'erreur du panier.
 * @property {HTMLElement} [annonce] - Zone lue par les lecteurs d'écran après l'ajout.
 *
 * @extends {Component<RefsArticles>}
 */
class MdArticlesLies extends Component {
  requiredRefs = ['compte', 'total', 'bouton'];

  /** @type {AbortController | undefined} */
  #controleur;

  /** @type {AbortController | null} */
  #envoi = null;

  /** @type {number | undefined} */
  #minuterieAnnonce;

  connectedCallback() {
    super.connectedCallback();

    this.#controleur?.abort();
    this.#controleur = new AbortController();
    const { signal } = this.#controleur;
    // Écouteurs natifs : avec on:change, le cadre d'Horizon remplacerait event.target par l'élément qui porte l'attribut.
    this.addEventListener('change', this.#surChangement, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#surQuantite, { signal });

    this.majSelection();
  }

  updatedCallback() {
    super.updatedCallback();
    this.majSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controleur?.abort();
    this.#envoi?.abort();
    clearTimeout(this.#minuterieAnnonce);
  }

  /**
   * Changement d'une case ou d'une quantité saisie au clavier.
   * @param {Event} event
   */
  #surChangement = (event) => {
    const cible = event.target;
    if (cible instanceof HTMLInputElement && cible.type === 'checkbox') {
      if (cible.checked) this.#exclusivite(cible);
    } else if (cible instanceof HTMLInputElement && cible.type === 'number') {
      this.#cocherLigne(cible);
    }
    this.majSelection();
  };

  /**
   * Quantité modifiée par − / + : la ligne est cochée d'office ; l'événement s'arrête ici.
   * @param {Event} event
   */
  #surQuantite = (event) => {
    event.stopPropagation();
    if (event.target instanceof Element) this.#cocherLigne(event.target);
    this.majSelection();
  };

  /**
   * Coche la ligne d'un sélecteur de quantité qu'on vient de modifier.
   * @param {Element} element
   */
  #cocherLigne(element) {
    const ligne = element.closest('[data-md-ligne]');
    const caseACocher = ligne?.querySelector('input[type="checkbox"]');
    if (caseACocher instanceof HTMLInputElement && !caseACocher.disabled && !caseACocher.checked) {
      caseACocher.checked = true;
      this.#exclusivite(caseACocher);
    }
  }

  /**
   * Une seule fiche cochée par groupe (ex. Care Refresh 1 an ou 2 ans).
   * @param {HTMLInputElement} caseCochee
   */
  #exclusivite(caseCochee) {
    const groupe = caseCochee.dataset.groupe;
    if (!groupe) return;
    for (const autre of this.querySelectorAll('input[type="checkbox"][data-groupe]')) {
      if (autre instanceof HTMLInputElement && autre !== caseCochee && autre.dataset.groupe === groupe) {
        autre.checked = false;
      }
    }
  }

  /** @returns {Array<{ id: number, quantite: number, ttc: number, ht: number }>} */
  #lignesChoisies() {
    const choix = [];
    for (const ligne of this.querySelectorAll('[data-md-ligne]')) {
      if (!(ligne instanceof HTMLElement)) continue;
      const caseACocher = ligne.querySelector('input[type="checkbox"]');
      if (!(caseACocher instanceof HTMLInputElement) || !caseACocher.checked || caseACocher.disabled) continue;

      const id = Number(ligne.dataset.varianteId);
      if (!id) continue;

      let quantite = 1;
      const champ = ligne.querySelector('input[type="number"]');
      if (champ instanceof HTMLInputElement) {
        const min = parseInt(champ.min, 10) || 1;
        const max = parseInt(champ.max, 10) || Infinity;
        quantite = Math.min(max, Math.max(min, parseInt(champ.value, 10) || min));
      }

      choix.push({
        id,
        quantite,
        ttc: Number(ligne.dataset.prixTtc) || 0,
        ht: Number(ligne.dataset.prixHt) || 0,
      });
    }
    return choix;
  }

  /**
   * « Sélection (n articles) » selon le nombre.
   * @param {number} nombre
   */
  #texteSelection(nombre) {
    const langue = document.documentElement.lang || 'fr';
    let forme = 'other';
    try {
      forme = new Intl.PluralRules(langue).select(nombre);
    } catch (erreur) {
      forme = nombre <= 1 ? 'one' : 'other';
    }
    const modele = (forme === 'one' ? this.dataset.texteUn : this.dataset.texteAutre) || this.dataset.texteAutre || '';
    return modele.replace('[n]', String(nombre));
  }

  /** Recalcule le compteur, le sous-total et l'état du bouton. */
  majSelection() {
    const { compte, total, bouton } = this.refs;
    if (!compte || !total || !bouton) return;

    const choix = this.#lignesChoisies();
    let nombre = 0;
    let ttc = 0;
    let ht = 0;
    for (const ligne of choix) {
      nombre += ligne.quantite;
      ttc += ligne.quantite * ligne.ttc;
      ht += ligne.quantite * ligne.ht;
    }

    compte.textContent = this.#texteSelection(nombre);

    total.dataset.priceTtc = String(ttc);
    total.dataset.priceHt = String(ht);
    const valeur = total.querySelector('[data-tax-price-value]');
    if (valeur) valeur.textContent = formaterPrix(modeTaxe() === 'ht' ? ht : ttc, this.dataset.devise || 'EUR');

    bouton.disabled = nombre === 0 || this.#envoi !== null;
  }

  /**
   * « Ajouter la sélection au panier » (attribut on:click="/ajouter" du bouton).
   * @param {Event} [event]
   */
  async ajouter(event) {
    event?.preventDefault();
    if (this.#envoi) return;

    const choix = this.#lignesChoisies();
    if (!choix.length) return;

    const { bouton } = this.refs;
    const nombre = choix.reduce((somme, ligne) => somme + ligne.quantite, 0);
    this.#masquerErreur();

    this.#envoi = new AbortController();
    bouton.disabled = true;
    bouton.setAttribute('aria-busy', 'true');

    // Sections du panier à renvoyer (tiroir), comme product-form.js.
    const sections = Array.from(
      new Set(
        Array.from(document.querySelectorAll('cart-items-component'), (element) =>
          element instanceof HTMLElement ? element.dataset.sectionId || '' : ''
        ).filter(Boolean)
      )
    );

    /** @type {Record<string, unknown>} */
    const corps = { items: choix.map(({ id, quantite }) => ({ id, quantity: quantite })) };
    if (sections.length) {
      corps.sections = sections.join(',');
      corps.sections_url = window.location.pathname;
    }

    try {
      const reponse = await fetch(routes().cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify(corps) }),
        signal: this.#envoi.signal,
      });
      const donnees = await reponse.json().catch(() => ({}));
      const icone = /** @type {any} */ (document.querySelector('cart-icon'));
      const nombreAvant = Number(icone?.currentCartCount) || 0;

      if (!reponse.ok || donnees.status) {
        // Refus de Shopify (ex. 422, stock) : message de Shopify, puis panier relu ; s'il a changé (ajout partiel,
        // non vérifié), le tiroir et la pastille sont rafraîchis.
        this.#afficherErreur(donnees.description || donnees.message || this.dataset.texteErreur || '');
        this.dispatchEvent(new CartErrorEvent(this.id, donnees.message, donnees.description, donnees.errors));
        const panier = await lirePanier();
        if (panier && panier.item_count !== nombreAvant) {
          this.dispatchEvent(
            new CartUpdateEvent(panier, this.id, { didError: true, source: SOURCE, itemCount: panier.item_count })
          );
        }
        return;
      }

      const panier = await lirePanier();
      const total = panier ? panier.item_count : nombreAvant + nombre;

      this.#annoncer(nombre);
      this.dispatchEvent(
        new CartAddEvent(panier || {}, this.id, {
          source: SOURCE,
          itemCount: total,
          sections: donnees.sections,
        })
      );
    } catch (erreur) {
      if (erreur instanceof Error && erreur.name === 'AbortError') return;
      console.error('t92 : ajout de la sélection impossible', erreur);
      this.#afficherErreur(this.dataset.texteErreur || '');
    } finally {
      this.#envoi = null;
      bouton.removeAttribute('aria-busy');
      this.majSelection();
    }
  }

  /** @param {string} message */
  #afficherErreur(message) {
    const { erreur } = this.refs;
    if (!erreur || !message) return;
    erreur.textContent = message;
    erreur.hidden = false;
  }

  #masquerErreur() {
    const { erreur } = this.refs;
    if (!erreur) return;
    erreur.hidden = true;
    erreur.textContent = '';
  }

  /**
   * « n articles ajoutés au panier » (traductions du thème, Theme.translations).
   * @param {number} nombre
   */
  #annoncer(nombre) {
    const { annonce } = this.refs;
    if (!annonce) return;
    const traductions = typeof Theme !== 'undefined' ? /** @type {any} */ (Theme).translations || {} : {};
    const texte =
      nombre === 1
        ? traductions.items_added_to_cart_one
        : String(traductions.items_added_to_cart_other || '').replace('{{ count }}', String(nombre));
    if (!texte) return;
    annonce.textContent = texte;
    clearTimeout(this.#minuterieAnnonce);
    this.#minuterieAnnonce = setTimeout(() => {
      annonce.textContent = '';
    }, 5000);
  }
}

if (!customElements.get('md-articles-lies')) {
  customElements.define('md-articles-lies', MdArticlesLies);
}
